import type { MetricAvailability } from '../../shared/analyst/api';
import { METRIC_LABEL, type FilterableMetric, type MetricFilterSet, type MetricRange } from '../../shared/analyst/run';

/**
 * Filtri sulle metriche per fonte (Criticita_dev.md, Fase 3, 3.8; decisione D7): preset con
 * un nome da dichiarare nel report, costruiti sulla copertura reale del dataset. Un preset
 * tocca solo le fonti in cui la metrica è presente in almeno metà delle righe.
 */

/** Copertura minima perché una fonte entri in un preset (sotto, il run segnala filter_low_coverage). */
export const PRESET_MIN_COVERAGE = 0.5;

export interface MetricPreset {
  id: string;
  label: string;
  metric: FilterableMetric;
  min: number;
}

export const METRIC_PRESETS: readonly MetricPreset[] = [
  { id: 'Escludi contenuti a zero interazioni', label: 'Escludi contenuti a zero interazioni', metric: 'interactions', min: 1 },
  { id: 'Solo account con seguito', label: 'Solo account con seguito (follower ≥ 1.000)', metric: 'followers', min: 1000 },
  { id: 'Solo testate con media reach', label: 'Solo testate con media reach ≥ 10.000', metric: 'media_reach', min: 10_000 },
];

/** Fonti del dataset in cui la metrica è abbastanza presente per filtrarla. */
export function coveredSources(av: MetricAvailability, metric: FilterableMetric): string[] {
  return av.sources.filter((s) => (s.metrics[metric] ?? 0) >= PRESET_MIN_COVERAGE).map((s) => s.source);
}

/** Intervalli di un preset: uno per fonte coperta, righe senza valore escluse. */
export function presetRanges(av: MetricAvailability, preset: MetricPreset): MetricRange[] {
  return coveredSources(av, preset.metric).map((source) => ({ sources: [source], metric: preset.metric, min: preset.min, max: null, missing: 'exclude' }));
}

export function rangeLabel(r: MetricRange): string {
  const n = (v: number): string => v.toLocaleString('it-IT');
  const bounds = r.min !== null && r.max !== null ? `tra ${n(r.min)} e ${n(r.max)}` : r.min !== null ? `≥ ${n(r.min)}` : r.max !== null ? `≤ ${n(r.max)}` : 'senza limiti';
  return `${r.sources.join(', ')}: ${METRIC_LABEL[r.metric]} ${bounds}${r.missing === 'exclude' ? ', senza valore escluse' : ', senza valore mantenute'}`;
}

export function describeMetricFilterSet(set: MetricFilterSet): string {
  return `${set.preset ? `«${set.preset}» — ` : ''}${set.ranges.map(rangeLabel).join('; ')}`;
}

/** Un intervallo è valido se ha almeno un limite e min non supera max. */
export function rangeValid(r: MetricRange): boolean {
  return (r.min !== null || r.max !== null) && (r.min === null || r.max === null || r.min <= r.max) && r.sources.length > 0;
}
